import { motion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};

/* ---------- shared card chrome ---------- */
function Card({ label, className = "", children, testid }) {
  return (
    <div
      data-testid={testid}
      className={`group relative flex h-full flex-col overflow-hidden rounded-xl border border-[#2A2A2A] bg-[#151515] transition-colors duration-500 hover:border-[#1F7A4D]/40 ${className}`}
    >
      <div className="flex items-center justify-between border-b border-[#2A2A2A] px-5 py-3">
        <span className="font-body text-[0.68rem] font-medium tracking-[0.22em] uppercase text-[#B3B3B3]">
          {label}
        </span>
        <span className="h-1.5 w-1.5 rounded-full bg-[#1F7A4D]" />
      </div>
      <div className="flex-1 p-5 md:p-6">{children}</div>
    </div>
  );
}

function Badge({ children }) {
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#2A2A2A] bg-[#0B0B0B] font-display text-base leading-none text-white">
      {children}
    </span>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <p className="font-display text-3xl leading-none text-white">{value}</p>
      <p className="mt-1.5 font-body text-[0.7rem] uppercase tracking-[0.14em] text-[#B3B3B3]">
        {label}
      </p>
    </div>
  );
}

const FORM = [
  { r: "W", win: true },
  { r: "W", win: true },
  { r: "D", draw: true },
  { r: "L" },
  { r: "W", win: true },
];

/* ---------- 1. Player Profile ---------- */
function PlayerProfile() {
  return (
    <Card label="Player Profile" testid="preview-player" className="lg:col-span-7">
      <div className="flex items-center gap-4">
        <span className="flex h-14 w-14 items-center justify-center rounded-full border border-[#1F7A4D]/40 bg-[#1F7A4D]/10 font-display text-xl text-white">
          AM
        </span>
        <div>
          <p className="font-display text-2xl leading-none tracking-wide text-white">
            Arjun Mehra
          </p>
          <p className="mt-1.5 font-body text-sm text-[#B3B3B3]">
            Forward · Hauz Khas United FC
          </p>
          <p className="font-body text-xs text-[#B3B3B3]/70">Hauz Khas, Delhi</p>
        </div>
      </div>

      <div className="mt-7 grid grid-cols-4 gap-4 border-t border-[#2A2A2A] pt-6">
        <Stat value="48" label="Matches" />
        <Stat value="27" label="Goals" />
        <Stat value="12" label="Assists" />
        <Stat value="6" label="MOTM" />
      </div>

      <div className="mt-6 flex items-center justify-between">
        <span className="font-body text-[0.7rem] uppercase tracking-[0.16em] text-[#B3B3B3]">
          Recent Form
        </span>
        <div className="flex gap-1.5">
          {FORM.map((f, i) => (
            <span
              key={i}
              className={`flex h-6 w-6 items-center justify-center rounded font-body text-[0.7rem] font-semibold ${
                f.win
                  ? "bg-[#1F7A4D] text-white"
                  : f.draw
                    ? "bg-[#2A2A2A] text-[#B3B3B3]"
                    : "border border-[#2A2A2A] text-[#B3B3B3]/60"
              }`}
            >
              {f.r}
            </span>
          ))}
        </div>
      </div>
    </Card>
  );
}

/* ---------- 2. Match Centre ---------- */
function MatchCentre() {
  return (
    <Card label="Match Centre" testid="preview-match" className="lg:col-span-5">
      <div className="flex items-center justify-between">
        <span className="rounded-full border border-[#2A2A2A] px-2.5 py-1 font-body text-[0.6rem] uppercase tracking-[0.18em] text-[#B3B3B3]">
          Full Time
        </span>
        <span className="font-body text-[0.68rem] text-[#B3B3B3]/70">
          Division 1 · Matchday 12
        </span>
      </div>

      <div className="mt-6 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <div className="flex flex-col items-center text-center">
          <Badge>HK</Badge>
          <p className="mt-2 font-body text-sm font-medium text-white">
            Hauz Khas United
          </p>
        </div>
        <p className="font-display text-4xl leading-none text-white">
          2<span className="mx-2 text-[#B3B3B3]/50">–</span>1
        </p>
        <div className="flex flex-col items-center text-center">
          <Badge>YR</Badge>
          <p className="mt-2 font-body text-sm font-medium text-white">
            Yamuna Rangers
          </p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 border-t border-[#2A2A2A] pt-5 text-center">
        <div className="font-body text-xs leading-relaxed text-[#B3B3B3]">
          A. Mehra 34&apos;
          <br />
          R. Chauhan 78&apos;
        </div>
        <div className="font-body text-xs leading-relaxed text-[#B3B3B3]">
          K. Verma 61&apos;
        </div>
      </div>

      <p className="mt-5 text-center font-body text-[0.7rem] uppercase tracking-[0.14em] text-[#B3B3B3]/70">
        Siri Fort Sports Complex
      </p>
    </Card>
  );
}

/* ---------- 3. Club Profile ---------- */
function ClubProfile() {
  return (
    <Card
      label="Community / Club"
      testid="preview-club"
      className="lg:col-span-4"
    >
      <div className="flex items-center gap-3">
        <Badge>SS</Badge>
        <div>
          <p className="font-display text-xl leading-none tracking-wide text-white">
            Saket Sporting Club
          </p>
          <p className="mt-1.5 font-body text-xs text-[#B3B3B3]">Saket, Delhi</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-3 border-t border-[#2A2A2A] pt-5">
        <Stat value="64" label="Members" />
        <Stat value="4" label="Teams" />
        <Stat value="132" label="Matches" />
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {["Seniors", "U-19", "U-17", "Women"].map((t) => (
          <span
            key={t}
            className="rounded-full border border-[#2A2A2A] px-3 py-1 font-body text-[0.7rem] text-[#B3B3B3]"
          >
            {t}
          </span>
        ))}
      </div>
    </Card>
  );
}

/* ---------- 4. Tournament / Fixtures ---------- */
const FIXTURES = [
  {
    d: "SAT 14 DEC",
    h: "Hauz Khas United",
    a: "Dwarka Athletic",
    v: "Dwarka Sports Complex",
  },
  {
    d: "SUN 15 DEC",
    h: "Saket Sporting",
    a: "Yamuna Rangers",
    v: "Siri Fort Complex",
  },
  {
    d: "WED 18 DEC",
    h: "Rohini FC",
    a: "Noida City",
    v: "Rohini Ground No. 4",
  },
];

function Tournament() {
  return (
    <Card
      label="Tournament"
      testid="preview-tournament"
      className="lg:col-span-4"
    >
      <p className="font-display text-xl leading-none tracking-wide text-white">
        Delhi NCR Premier Cup
      </p>
      <p className="mt-1.5 font-body text-xs uppercase tracking-[0.14em] text-[#1F7A4D]">
        Upcoming Fixtures
      </p>

      <div className="mt-5 flex flex-col divide-y divide-[#2A2A2A] border-t border-[#2A2A2A]">
        {FIXTURES.map((f, i) => (
          <div key={i} className="py-3.5">
            <p className="font-body text-[0.62rem] uppercase tracking-[0.16em] text-[#B3B3B3]/70">
              {f.d}
            </p>
            <p className="mt-1 font-body text-sm text-white">
              {f.h} <span className="text-[#B3B3B3]/50">vs</span> {f.a}
            </p>
            <p className="font-body text-[0.7rem] text-[#B3B3B3]">{f.v}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ---------- 5. League Table ---------- */
const TABLE = [
  { c: "Hauz Khas United FC", p: 12, w: 9, d: 2, l: 1, pts: 29 },
  { c: "Saket Sporting Club", p: 12, w: 8, d: 2, l: 2, pts: 26 },
  { c: "Yamuna Rangers FC", p: 12, w: 7, d: 3, l: 2, pts: 24 },
  { c: "Dwarka Athletic", p: 12, w: 5, d: 4, l: 3, pts: 19 },
  { c: "Mayur Vihar FC", p: 12, w: 4, d: 3, l: 5, pts: 15 },
];

function LeagueTable() {
  return (
    <Card
      label="League Table"
      testid="preview-league"
      className="lg:col-span-4"
    >
      <p className="font-display text-xl leading-none tracking-wide text-white">
        Delhi NCR League
      </p>
      <p className="mt-1.5 font-body text-xs text-[#B3B3B3]">Division 1</p>

      <div className="mt-5">
        <div className="grid grid-cols-[1.6rem_1fr_1.4rem_1.4rem_1.4rem_1.4rem_2rem] items-center gap-1 border-b border-[#2A2A2A] pb-2 font-body text-[0.6rem] uppercase tracking-[0.1em] text-[#B3B3B3]/60">
          <span>#</span>
          <span>Club</span>
          <span className="text-center">P</span>
          <span className="text-center">W</span>
          <span className="text-center">D</span>
          <span className="text-center">L</span>
          <span className="text-right">Pts</span>
        </div>
        {TABLE.map((row, i) => (
          <div
            key={row.c}
            className="grid grid-cols-[1.6rem_1fr_1.4rem_1.4rem_1.4rem_1.4rem_2rem] items-center gap-1 border-b border-[#2A2A2A]/60 py-2.5 font-body text-[0.72rem] text-[#B3B3B3]"
          >
            <span
              className={
                i === 0 ? "font-semibold text-[#1F7A4D]" : "text-[#B3B3B3]/70"
              }
            >
              {i + 1}
            </span>
            <span className="truncate text-white">{row.c}</span>
            <span className="text-center">{row.p}</span>
            <span className="text-center">{row.w}</span>
            <span className="text-center">{row.d}</span>
            <span className="text-center">{row.l}</span>
            <span
              className={`text-right font-semibold ${
                i === 0 ? "text-[#1F7A4D]" : "text-white"
              }`}
            >
              {row.pts}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ---------- Section ---------- */
const CARDS = [
  PlayerProfile,
  MatchCentre,
  ClubProfile,
  Tournament,
  LeagueTable,
];

export default function PreviewCollective() {
  return (
    <section
      id="preview"
      data-testid="preview-collective"
      className="relative z-20 border-t border-[#2A2A2A] bg-[#0B0B0B] py-20 md:py-28"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        {/* Eyebrow + Concept label */}
        <div className="flex items-start justify-between gap-4">
          <motion.span
            {...fadeUp}
            transition={{ duration: 0.8, ease: EASE }}
            className="flex items-center gap-3"
          >
            <span className="h-2 w-2 rounded-full bg-[#1F7A4D]" />
            <span className="font-body text-xs md:text-sm font-medium tracking-[0.3em] uppercase text-[#B3B3B3]">
              Product Preview
            </span>
          </motion.span>

          <motion.span
            {...fadeUp}
            transition={{ duration: 0.8, ease: EASE, delay: 0.05 }}
            data-testid="preview-concept-label"
            className="flex items-center gap-2 rounded-full border border-[#2A2A2A] px-3 py-1.5"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#1F7A4D]" />
            <span className="font-body text-[0.62rem] md:text-[0.68rem] font-medium tracking-[0.2em] uppercase text-[#B3B3B3]">
              Concept · Not Final
            </span>
          </motion.span>
        </div>

        {/* Heading + supporting */}
        <div className="mt-7 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between lg:gap-16">
          <motion.h2
            {...fadeUp}
            transition={{ duration: 0.9, ease: EASE, delay: 0.08 }}
            data-testid="preview-heading"
            className="font-display uppercase text-white text-5xl md:text-6xl lg:text-7xl leading-[0.88] tracking-[0.01em]"
          >
            A First Look At
            <br />
            The Collective.
          </motion.h2>
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.9, ease: EASE, delay: 0.14 }}
            className="max-w-[400px] font-body text-lg font-light leading-relaxed text-[#B3B3B3] lg:text-right"
          >
            A first look at how player identities, community records, match
            history and football data can come together in one place.
          </motion.p>
        </div>

        {/* Mockup grid */}
        <div className="mt-14 md:mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-12">
          {CARDS.map((C, i) => {
            const lgSpan = [
              "lg:col-span-6",
              "lg:col-span-6",
              "lg:col-span-4",
              "lg:col-span-4",
              "lg:col-span-4",
            ][i];
            return (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ duration: 0.8, ease: EASE, delay: 0.05 * i }}
                className={`h-full ${lgSpan} ${i === 4 ? "md:col-span-2 lg:col-span-4" : ""}`}
              >
                <C />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
